import React, { useContext, useState } from 'react';
import { FuelTypography } from '@frete.com/fuel-react';
import { FormProvider, useForm } from 'react-hook-form';
import { FuelBottomSheet } from '@frete.com/fuel-station-react';
import { zodResolver } from '@hookform/resolvers/zod';
import Dialog from '@components/ui/Dialog/Dialog';
import AppContext from '@context';
import useAmplitude from '@hooks/useAmplitude';
import { unrecognizedJourney } from '@services/journeys';
import Toast, { ToastPropsInterface } from '@components/ui/Toast';
import { unrecognizedSchema } from './Form/schema';
import Form, { SchemaType } from './Form';
import { DialogUnrecognizedPropsInterface } from './DialogUnrecognizedTypes';

function DialogUnrecognized({
  isMobile,
  onSuccess,
}: DialogUnrecognizedPropsInterface) {
  const {
    state: { ui },
    dispatch,
  } = useContext(AppContext);
  const { amplitudeEvent } = useAmplitude();
  const unrecognizedHook = useForm<SchemaType>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(unrecognizedSchema),
  });
  const { watch } = unrecognizedHook;
  const reason = watch('reason');
  const { isShow, journeyId } = ui.DialogUnrecognized;
  const toastDataDefault: ToastPropsInterface = {
    isShow: false,
    title: '',
    slug: 'journey_unrecognized',
    severity: 'neutral',
  };
  const [isLoading, setLoading] = useState(false);
  const [toastData, setToastData] = useState<ToastPropsInterface>({
    ...toastDataDefault,
  });
  const textButtonSubmit = 'Confirmar e encerrar';

  function handleSubmit() {
    const payload = {
      reason,
    };

    setLoading(true);

    unrecognizedJourney({ journeyId, payload })
      .then(() => {
        const message = 'Viagem arquivada com sucesso!';

        amplitudeEvent('journey_unrecognized_confirmed', {
          source: 'kebab-viagem',
          journey_id: journeyId,
        });

        setToastData({
          ...toastDataDefault,
          isShow: true,
          title: message,
          severity: 'success',
        });
        amplitudeEvent('journey_unrecognized_toast_show', {
          source: 'kebab-viagem',
          journey_id: journeyId,
          status_code: 200,
          message,
        });
        dispatch({
          type: 'ui_dialogUnrecognized_set_show',
          payload: { isShow: false, journeyId: '' },
        });
        onSuccess();
        unrecognizedHook.reset();
      })
      .catch((error) => {
        const message = 'Não foi possível realizar esta ação';
        setToastData({
          ...toastDataDefault,
          isShow: true,
          title: message,
          message: 'Tente novamente mais tarde.',
          severity: 'error',
        });
        amplitudeEvent('journey_unrecognized_toast_show', {
          source: 'kebab-viagem',
          journey_id: journeyId,
          status_code: error.status,
          message,
        });
      })
      .finally(() => setLoading(false));
  }

  function onClickConfirm() {
    handleSubmit();
  }

  function handleClose(where?: 'btnClose' | 'btnCancel' | 'overlay') {
    const mapSource = {
      btnClose: 'x-fechar-nao-reconhecimento-viagem',
      btnCancel: 'cancelar-nao-reconhecimento-viagem',
      overlay: 'area-cinza-nao-reconhecimento-viagem',
    };

    amplitudeEvent('journey_list_in_progress_viewed', {
      source: mapSource[where],
    });
    unrecognizedHook.reset();
    dispatch({
      type: 'ui_dialogUnrecognized_set_show',
      payload: { isShow: false, journeyId: '' },
    });
  }

  function resetToast() {
    setToastData(toastDataDefault);
  }

  function handleRenderComponent() {
    return (
      <>
        <FuelBottomSheet
          title="Tem certeza que não reconhece esta viagem?"
          confirmText={textButtonSubmit}
          isOpen={isShow && isMobile}
          onClose={() => handleClose('btnClose')}
          refuseText="Cancelar"
          onConfirm={() => {
            onClickConfirm();
          }}
          onRefuse={() => handleClose('btnCancel')}
          isLoading={isLoading}
          portalName="journey-web-unrecognized-journey"
          height={432}
          zIndex={1001}
        >
          <FuelTypography
            size="body-md"
            weight="medium"
            color="neutral-default"
            spacingTop={16}
          >
            Clicando em ”Confirmar e encerrar”, a mesma, não ficará mais
            disponível para monitoramento e será movida para aba ”Encerradas”.
          </FuelTypography>
          <FormProvider {...unrecognizedHook}>
            <Form onSubmit={() => onClickConfirm()} />
          </FormProvider>
        </FuelBottomSheet>

        <div data-testid="dialog-unrecognized">
          <Dialog
            title="Tem certeza que não reconhece esta viagem?"
            confirmText={textButtonSubmit}
            isOpen={isShow && !isMobile}
            onClose={(where) => handleClose(where)}
            refuseText="Cancelar"
            onConfirm={() => onClickConfirm()}
            onRefuse={() => handleClose('btnCancel')}
            width="xs"
            spacing={3}
            isLoading={isLoading}
          >
            <FuelTypography
              size="body-md"
              weight="medium"
              color="neutral-default"
            >
              Clicando em ”{textButtonSubmit}”, a mesma, não ficará mais
              disponível para monitoramento e será movida para aba ”Encerradas”.
            </FuelTypography>
            <FormProvider {...unrecognizedHook}>
              <Form onSubmit={() => onClickConfirm()} />
            </FormProvider>
          </Dialog>
        </div>
      </>
    );
  }

  return (
    <>
      {handleRenderComponent()}
      <Toast {...toastData} onClose={() => resetToast()} />
    </>
  );
}

export default DialogUnrecognized;
